import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  QueryList,
  ViewChild,
  ViewChildren,
  ChangeDetectionStrategy,
} from "@angular/core";
import {
  ActiveUser,
  ProfilePromiseActionService,
  ProfileSettingKeys,
  Roles,
  SysFeatures,
  SystemConfigPromiseService,
  UserPromiseLookupService,
} from "shared/cubiq-services";

import { TranslateService } from "@ngx-translate/core";
import { ProfileUpdateRequestImpl } from "shared/cubiq-model-multi-asset";
import {
  IBfxUserSettingsPageSubComponent,
  BfxUserSettingsValidityState,
} from "app/bfx-user-settings/bfx-user-settings-page.component";
import { BfxUserSettingsPageComponent } from "./bfx-user-settings-page.component";
import { BfxUserSettingsService, BfxUserSettingsComponentState } from "./bfx-user-settings.service";
import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

@Component({
  selector: ".bfx-order-entry-settings",
  templateUrl: "./html/bfx-order-entry-settings.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class BfxOrderEntrySettingsComponent extends BfxUserSettingsPageComponent {
  /***************************************/
  /* DATA */
  public dataChanged: boolean = false;
  public activeSubComponent: number = 3;
  private activeTab: number = 0;

  @ViewChildren("subComponent") subComponents: QueryList<
    IBfxUserSettingsPageSubComponent<BfxOrderEntrySettingsComponent>
  >;

  public entryType: any = {
    dataset: [],
    suggestions: [],
    disabled: false,
    value: null, // BfxAutocompleteGenericModel
  };
  public ROLE_VIEW_ORDER_ENTRY: boolean = true;

  /**********************************************************************/
  /* CONSTRUCTOR  */

  constructor(
    public userSettingsService: BfxUserSettingsService,
    public systemConfigService: SystemConfigPromiseService,
    public profileService: ProfilePromiseActionService,
    public userService: UserPromiseLookupService,
    public element: ElementRef,
    public translationService: TranslateService,
    public cd: ChangeDetectorRef,
  ) {
    super();
    this.initializeEntryTypeDataset();
  }

  /**********************************************************************/
  /* INITIALIZATION */

  private initializeEntryTypeDataset(): void {
    this.systemConfigService.hasRole(Roles.VIEW_ORDER_ENTRY).then((value) => {
      this.ROLE_VIEW_ORDER_ENTRY = value;
    });

    Promise.all([
      this.systemConfigService.canEnterOrder(),
      this.systemConfigService.canEnterAlgoOrder(),
      this.systemConfigService.canEnterNdfOrder(),
      this.systemConfigService.getSystemConfig(SysFeatures.fwdEnabled),
      this.systemConfigService.canEnterBmarkOrder(),
      this.systemConfigService.canEnterGammaOrder(),
    ]).then((values: any) => {
      let canEnterSpotOrder = values[0];
      let canEnterAlgoOrder = values[1];
      let canEnterNdfOrder = values[2];
      let fwdEnabled = values[3].toUpperCase() == "Y";
      let canEntryBmarkOrder = values[4];
      let canEnterGammaOrder = values[5];

      // SPOT ENTRY
      if (canEnterSpotOrder) {
        let labelSpot: string = fwdEnabled
          ? this.translationService.instant("entry_defaults.spotFwd.title")
          : this.translationService.instant("entry_defaults.spot.title");
        this.entryType.dataset.push({ label: labelSpot, value: "spot" });
        this.entryType.value = { label: labelSpot, value: "spot" };
      }
      // NDF ENTRY
      if (canEnterNdfOrder) {
        let labelNdf: string = this.translationService.instant("entry_defaults.ndf.title");
        this.entryType.dataset.push({ label: labelNdf, value: "ndf" });
        this.entryType.value =
          this.entryType.value != null && this.entryType.value != ""
            ? this.entryType.value
            : { label: labelNdf, value: "ndf" };
      }
      // Gamma ENTRY
      if (canEnterGammaOrder) {
        let labelGamma: string = this.translationService.instant("entry_defaults.gamma.title");
        this.entryType.dataset.push({ label: labelGamma, value: "gamma" });
        if (this.entryType.value == null || this.entryType.value == "") {
          this.entryType.value = { label: labelGamma, value: "gamma" };
        }
      }
      // ALGO ENTRY
      if (canEnterAlgoOrder) {
        let labelAlgo: string = this.translationService.instant("entry_defaults.algo.title");
        this.entryType.dataset.push({ label: labelAlgo, value: "algo" });
        this.entryType.value =
          this.entryType.value != null && this.entryType.value != ""
            ? this.entryType.value
            : { label: labelAlgo, value: "algo" };
      }

      // ALGO NDF ENTRY
      if (canEnterAlgoOrder && canEnterNdfOrder) {
        let labelAlgo: string = this.translationService.instant("entry_defaults.ndf.algo.title");
        this.entryType.dataset.push({ label: labelAlgo, value: "ndfalgo" });
        this.entryType.value =
          this.entryType.value != null && this.entryType.value != ""
            ? this.entryType.value
            : { label: labelAlgo, value: "ndfalgo" };
      }

      // Benchmark ENTRY
      if (canEntryBmarkOrder) {
        let labelBmark: string = this.translationService.instant("entry_defaults.benchmark.title");
        this.entryType.dataset.push({ label: labelBmark, value: "bmark" });
        this.entryType.value =
          this.entryType.value != null && this.entryType.value != ""
            ? this.entryType.value
            : { label: labelBmark, value: "bmark" };
      }

      this.entryType.suggestions = Object.assign([], this.entryType.dataset);

      if (canEnterSpotOrder || canEnterNdfOrder || canEnterAlgoOrder || canEnterGammaOrder) {
        this.entryType.disabled = false;
      } else {
        this.entryType.disabled = true;
      }
    });
  }

  /**********************************************************************/
  /* TABs & Entry type dropdown */

  public onTabActivated(event: any): void {
    this.activeTab = event.index;
  }

  public entryTypeChange($event: any): void {
    //logger.out.warn(`Entry Type Change `, $event, ` | model: `, this.entryType.value);
  }

  public entryTypeSearch($event: any): void {
    this.entryType.suggestions = Object.assign([], this.entryType.dataset);
  }

  /**********************************************************************/
  /* SETTINGS METHODS */
  /**********************************************************************/

  public initialize(): void {
    this.subComponents.forEach((subComponent) => {
      setImmediate(() => {
        subComponent.initialize();
      });
    });
  }

  public validate(): Promise<BfxUserSettingsValidityState> {
    let promises: Promise<BfxUserSettingsValidityState>[] = [];
    let delay = function (t, val) {
      return new Promise((resolve) => {
        setTimeout(resolve.bind(null, val), t);
      });
    };
    let raceAll = function (promises, timeoutTime, timeoutVal) {
      return Promise.all(
        promises.map((p) => {
          return Promise.race([p, delay(timeoutTime, timeoutVal)]);
        }),
      );
    };

    this.subComponents.forEach((subComponent) => {
      let subComponentDataChanged = subComponent.hasDataChanged();
      if (typeof subComponentDataChanged === "boolean" && subComponentDataChanged) {
        let promise = subComponent.validate();
        promises.push(promise);
      }
    });

    return new Promise<BfxUserSettingsValidityState>((res, reject) => {
      (raceAll(promises, 300, BfxUserSettingsValidityState.INVALID) as any).then(
        (values) => {
          let subComponentHasInvalid: boolean = values.some((value, index) => {
            let hasInvalid = value === BfxUserSettingsValidityState.INVALID;
            //logger.out.info(`${this.constructor.name}.validate() values[${index}] %O hasInvalid:${hasInvalid}`, value);
            return hasInvalid;
          });
          let subComponentHasActiveUnsaveble: boolean = values.some((value, index) => {
            let hasUnsaveable = value === BfxUserSettingsValidityState.ACTIVE_UNSAVEABLE;
            //logger.out.info(`${this.constructor.name}.validate() values[${index}] %O hasUnsaveable:${hasUnsaveable}`, value);
            return hasUnsaveable;
          });
          let subComponentValid: boolean = values.every((value, index) => {
            let hasValid = value === BfxUserSettingsValidityState.VALID;
            //logger.out.info(`${this.constructor.name}.validate() values[${index}] %O valid:${hasValid}`, value);
            return hasValid;
          });

          let state;

          if (subComponentHasInvalid) {
            state = BfxUserSettingsValidityState.INVALID;
          } else if (subComponentHasActiveUnsaveble) {
            state = BfxUserSettingsValidityState.ACTIVE_UNSAVEABLE;
          } else {
            state = subComponentValid
              ? BfxUserSettingsValidityState.VALID
              : BfxUserSettingsValidityState.INVALID;
          }
          //logger.out.info(`${this.constructor.name}.validate() valid:BfxUserSettingsValidityState.${BfxUserSettingsValidityState[state]}`);
          res(state);
        },
        (err) => {
          reject(err);
        },
      );
    });
  }

  public reset(): Promise<void> {
    let promises: Promise<void>[] = [];

    this.subComponents.forEach((subComponent) => {
      let subComponentDataChanged = subComponent.hasDataChanged();
      if (typeof subComponentDataChanged === "boolean" && subComponentDataChanged) {
        let promise = subComponent.reset();
        promises.push(promise);
      }
    });

    return new Promise<void>((res, reject) => {
      Promise.all(promises).then(
        (values) => {
          // run any reset logic for this.reset now
          // as subComponents did not reject reset
          this.cd.detectChanges();
          res();
        },
        () => {
          reject();
        },
      );
    });
  }

  public postReset(): void {
    this.dataChanged = false;

    this.subComponents.forEach((subComponent) => {
      subComponent.postReset();
    });
  }

  public save(request: ProfileUpdateRequestImpl): void {
    this.subComponents.forEach((subComponent) => {
      subComponent.save(request);
    });
  }

  public postSave(request: ProfileUpdateRequestImpl): void {
    this.dataChanged = false;

    this.subComponents.forEach((subComponent) => {
      subComponent.postSave(request);
    });
  }

  public onComponentStateChanged(componentState: BfxUserSettingsComponentState): void {
    this.subComponents.forEach((subComponent) => {
      subComponent.onComponentStateChanged(componentState);
    });
  }
}
